import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Stack, TextField, IconButton, Chip,
  Tooltip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, ToggleButton, ToggleButtonGroup,
  List, ListItemButton, ListItemText, ListSubheader,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  ClearAll as ClearIcon,
  ContentCopy as CopyIcon,
  ContentPaste as ContentPasteIcon,
  Add as AddIcon,
  PhotoLibrary as CollectionIcon,
  Photo as PageIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import albumService from '../api/albumService';


// ── A4 Landscape constants ──────────────────────────────────────────────────
const PAGE_W_MM = 297;
const PAGE_H_MM = 210;
const TITLE_STRIP_MM = 16;  // reserved title band at top of page
const ZONE_GAP_MM = 4;      // gap inset on every side (matches backend ZONE_GAP_MM)
const CONTENT_H_MM = PAGE_H_MM - TITLE_STRIP_MM; // 194mm
const CANVAS_W_PX = 620;
const SCALE = CANVAS_W_PX / PAGE_W_MM;            // px per mm
const CANVAS_H_PX = Math.round(PAGE_H_MM * SCALE);
const MIN_SLOT_MM = 12;
const HANDLE_HIT_PX = 22; // total clickable hit area (easier to grab)
const HANDLE_VIS_PX = 12; // visible dot size

// Colors for slots (cycle through these)
const SLOT_COLORS = [
  '#bbdefb', '#f8bbd0', '#c8e6c9', '#fff9c4', '#f3e5f5',
  '#b2ebf2', '#ffe0b2', '#d7ccc8', '#dcedc8', '#fce4ec',
];

// ── Coordinate conversions ──────────────────────────────────────────────────

/** Convert rendered mm (editor) coordinates to backend pct format */
function mmToPct(x, y, w, h) {
  return {
    xPct: parseFloat(((x - ZONE_GAP_MM) / PAGE_W_MM).toFixed(4)),
    yPct: parseFloat(((y - TITLE_STRIP_MM - ZONE_GAP_MM) / CONTENT_H_MM).toFixed(4)),
    wPct: parseFloat(((w + ZONE_GAP_MM * 2) / PAGE_W_MM).toFixed(4)),
    hPct: parseFloat(((h + ZONE_GAP_MM * 2) / CONTENT_H_MM).toFixed(4)),
  };
}

/** Convert backend pct format to rendered mm (same as formatTemplatesForPrompt) */
function pctToMm(xPct, yPct, wPct, hPct, rotate) {
  return {
    x: Math.round(xPct * PAGE_W_MM) + ZONE_GAP_MM,
    y: Math.round(yPct * CONTENT_H_MM) + TITLE_STRIP_MM + ZONE_GAP_MM,
    width: Math.max(MIN_SLOT_MM, Math.round(wPct * PAGE_W_MM) - ZONE_GAP_MM * 2),
    height: Math.max(MIN_SLOT_MM, Math.round(hPct * CONTENT_H_MM) - ZONE_GAP_MM * 2),
    rotate: rotate || 0,
  };
}

/** Clamp slot so it stays within page bounds (below title strip) */
function clampSlot(x, y, w, h) {
  const minY = TITLE_STRIP_MM + ZONE_GAP_MM; // 20mm
  const cw = Math.max(MIN_SLOT_MM, Math.min(PAGE_W_MM - 2 * ZONE_GAP_MM, w));
  const ch = Math.max(MIN_SLOT_MM, Math.min(PAGE_H_MM - minY - ZONE_GAP_MM, h));
  const cx = Math.max(ZONE_GAP_MM, Math.min(PAGE_W_MM - ZONE_GAP_MM - cw, x));
  const cy = Math.max(minY, Math.min(PAGE_H_MM - ZONE_GAP_MM - ch, y));
  return { x: cx, y: cy, width: cw, height: ch };
}

let nextSlotId = 1;
function makeSlotId() { return `slot_${nextSlotId++}`; }

// Helper: convert custom page template (pct zones) to flat mm slots for thumbnail display
function pageTemplateToMmSlots(pt) {
  const result = [];
  (pt.pages || []).forEach(p => {
    (p.zones || []).forEach(z => {
      result.push(pctToMm(z.xPct, z.yPct, z.wPct, z.hPct, z.rotate));
    });
  });
  return result;
}

// ── Unified SVG thumbnail ─────────────────────────────────────────────────────
// Accepts slotsInMm: array of {x, y, width, height} in mm
function PageThumb({ slotsInMm = [], size = 70 }) {
  const scale = size / PAGE_W_MM;
  const h = Math.round(PAGE_H_MM * scale);
  return (
    <svg width={size} height={h} style={{ display: 'block', border: '1px solid #ccc', borderRadius: 2 }}>
      <rect x={0} y={0} width={size} height={h} fill="#f5f5f5" />
      <rect x={0} y={0} width={size} height={Math.round(TITLE_STRIP_MM * scale)} fill="#e0e0e0" />
      {slotsInMm.map((sl, i) => {
        const rx = Math.round(sl.x * scale);
        const ry = Math.round(sl.y * scale);
        const rw = Math.round(sl.width * scale);
        const rh = Math.round(sl.height * scale);
        const rot = sl.rotate || 0;
        const cx = rx + rw / 2;
        const cy = ry + rh / 2;
        return (
          <rect key={i}
            x={rx} y={ry}
            width={rw} height={rh}
            fill={SLOT_COLORS[i % SLOT_COLORS.length]}
            stroke="#aaa" strokeWidth={0.5}
            transform={rot ? `rotate(${rot} ${cx} ${cy})` : undefined}
          />
        );
      })}
    </svg>
  );
}

// ── Dual-page thumbnail for pageBudget=2 templates ───────────────────────────
// Shows page 0 and page 1 as two side-by-side mini thumbs
function SpreadThumb({ pt, size = 110 }) {
  if (!pt.pages || (pt.pageBudget ?? 1) < 2) {
    const mmSlots = pt.slotsInMm || pageTemplateToMmSlots(pt);
    return <PageThumb slotsInMm={mmSlots} size={size} />;
  }
  const halfSize = Math.round((size - 2) / 2);
  const page0Slots = (pt.pages || [])
    .filter(p => p.pageIndex === 0)
    .flatMap(p => (p.zones || []).map(z => pctToMm(z.xPct, z.yPct, z.wPct, z.hPct, z.rotate)));
  const page1Slots = (pt.pages || [])
    .filter(p => p.pageIndex === 1)
    .flatMap(p => (p.zones || []).map(z => pctToMm(z.xPct, z.yPct, z.wPct, z.hPct, z.rotate)));
  return (
    <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
      <PageThumb slotsInMm={page0Slots} size={halfSize} />
      <PageThumb slotsInMm={page1Slots} size={halfSize} />
    </Box>
  );
}

// ── Single page canvas ───────────────────────────────────────────────────────
function PageCanvas({ slots, selectedId, onSelectSlot, onAddSlot, onMoveSlot, onResizeSlot, pageIndex }) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const getCanvasPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / SCALE,
      y: (e.clientY - rect.top) / SCALE,
    };
  }, []);

  const handleCanvasMouseDown = useCallback((e) => {
    if (e.target !== canvasRef.current) return; // click on slot, not canvas bg
    e.preventDefault();
    const pos = getCanvasPos(e);
    dragRef.current = { type: 'create', startX: pos.x, startY: pos.y, pageIndex };

    const onMove = (me) => {
      if (!dragRef.current || dragRef.current.type !== 'create') return;
      dragRef.current.currentX = (me.clientX - canvasRef.current.getBoundingClientRect().left) / SCALE;
      dragRef.current.currentY = (me.clientY - canvasRef.current.getBoundingClientRect().top) / SCALE;
      // live ghost — do nothing special, will resolve on mouseup
    };
    const onUp = (me) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (!dragRef.current || dragRef.current.type !== 'create') return;
      const { startX, startY } = dragRef.current;
      const endX = (me.clientX - canvasRef.current.getBoundingClientRect().left) / SCALE;
      const endY = (me.clientY - canvasRef.current.getBoundingClientRect().top) / SCALE;
      const raw = {
        x: Math.min(startX, endX), y: Math.min(startY, endY),
        width: Math.abs(endX - startX), height: Math.abs(endY - startY),
      };
      if (raw.width < 4 || raw.height < 4) { dragRef.current = null; return; }
      const clamped = clampSlot(raw.x, raw.y, raw.width, raw.height);
      onAddSlot(pageIndex, clamped);
      dragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [getCanvasPos, onAddSlot, pageIndex]);

  const handleSlotMouseDown = useCallback((e, slotId, handle) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectSlot(slotId);
    const slot = slots.find(s => s.id === slotId);
    if (!slot) return;

    dragRef.current = {
      type: handle === 'move' ? 'move' : 'resize',
      handle,
      slotId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origX: slot.x, origY: slot.y,
      origW: slot.width, origH: slot.height,
    };

    const onMove = (me) => {
      if (!dragRef.current) return;
      const dx = (me.clientX - dragRef.current.startClientX) / SCALE;
      const dy = (me.clientY - dragRef.current.startClientY) / SCALE;
      const { origX, origY, origW, origH, handle: h } = dragRef.current;

      let nx = origX, ny = origY, nw = origW, nh = origH;
      if (h === 'move') {
        nx = origX + dx; ny = origY + dy;
      } else {
        if (h === 'se') { nw = origW + dx; nh = origH + dy; }
        else if (h === 'sw') { nx = origX + dx; nw = origW - dx; nh = origH + dy; }
        else if (h === 'ne') { ny = origY + dy; nw = origW + dx; nh = origH - dy; }
        else if (h === 'nw') { nx = origX + dx; ny = origY + dy; nw = origW - dx; nh = origH - dy; }
        else if (h === 'n')  { ny = origY + dy; nh = origH - dy; }
        else if (h === 's')  { nh = origH + dy; }
        else if (h === 'e')  { nw = origW + dx; }
        else if (h === 'w')  { nx = origX + dx; nw = origW - dx; }
      }
      const clamped = clampSlot(nx, ny, nw, nh);
      (dragRef.current.type === 'move' ? onMoveSlot : onResizeSlot)(slotId, clamped);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      dragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [slots, onSelectSlot, onMoveSlot, onResizeSlot]);

  const titleStripPx = TITLE_STRIP_MM * SCALE;

  return (
    <Box
      ref={canvasRef}
      onMouseDown={handleCanvasMouseDown}
      sx={{
        position: 'relative',
        width: CANVAS_W_PX,
        height: CANVAS_H_PX,
        bgcolor: '#fafafa',
        border: '2px solid #bbb',
        borderRadius: 1,
        cursor: 'crosshair',
        flexShrink: 0,
        userSelect: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Title strip */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: titleStripPx,
        bgcolor: '#e8e8e8', borderBottom: '1px dashed #bbb', display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <Typography sx={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>Title Strip (reserved)</Typography>
      </Box>

      {/* Grid lines at 50% */}
      <Box sx={{ position: 'absolute', top: 0, left: '50%', width: 1, height: '100%', bgcolor: 'rgba(0,0,0,0.06)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', top: '50%', left: 0, width: '100%', height: 1, bgcolor: 'rgba(0,0,0,0.06)', pointerEvents: 'none' }} />

      {/* Page index label */}
      <Typography sx={{ position: 'absolute', bottom: 4, right: 6, fontSize: 10, color: '#bbb', pointerEvents: 'none' }}>
        Page {pageIndex + 1}
      </Typography>

      {/* Slots */}
      {slots.map((slot, si) => {
        const left = slot.x * SCALE;
        const top = slot.y * SCALE;
        const width = slot.width * SCALE;
        const height = slot.height * SCALE;
        const isSelected = slot.id === selectedId;
        const color = SLOT_COLORS[si % SLOT_COLORS.length];

        const handles = isSelected ? ['n','s','e','w','ne','nw','se','sw'] : [];
        const handlePositions = {
          n:  { top: -HANDLE_HIT_PX/2, left: width/2 - HANDLE_HIT_PX/2 },
          s:  { top: height - HANDLE_HIT_PX/2, left: width/2 - HANDLE_HIT_PX/2 },
          e:  { top: height/2 - HANDLE_HIT_PX/2, left: width - HANDLE_HIT_PX/2 },
          w:  { top: height/2 - HANDLE_HIT_PX/2, left: -HANDLE_HIT_PX/2 },
          ne: { top: -HANDLE_HIT_PX/2, left: width - HANDLE_HIT_PX/2 },
          nw: { top: -HANDLE_HIT_PX/2, left: -HANDLE_HIT_PX/2 },
          se: { top: height - HANDLE_HIT_PX/2, left: width - HANDLE_HIT_PX/2 },
          sw: { top: height - HANDLE_HIT_PX/2, left: -HANDLE_HIT_PX/2 },
        };
        const cursors = { n:'ns-resize', s:'ns-resize', e:'ew-resize', w:'ew-resize', ne:'nesw-resize', sw:'nesw-resize', se:'nwse-resize', nw:'nwse-resize' };

        return (
          <Box
            key={slot.id}
            onMouseDown={(e) => handleSlotMouseDown(e, slot.id, 'move')}
            sx={{
              position: 'absolute', left, top, width, height,
              bgcolor: color,
              border: isSelected ? '2px solid #1565c0' : '1.5px solid rgba(0,0,0,0.25)',
              borderRadius: 0.5,
              cursor: 'move',
              boxSizing: 'border-box',
              overflow: 'visible',
              transform: slot.rotate ? `rotate(${slot.rotate}deg)` : undefined,
              transformOrigin: 'center center',
            }}
          >
            {/* Inner content clipped within slot bounds */}
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography sx={{ fontSize: Math.max(9, Math.min(13, height * 0.18)), color: 'rgba(0,0,0,0.55)', fontWeight: 600, textAlign: 'center', px: 0.5 }} noWrap>
                {slot.role || `zone ${si + 1}`}
              </Typography>
              <Typography sx={{ position: 'absolute', bottom: 3, right: 4, fontSize: 11, color: 'rgba(0,0,0,0.55)', fontWeight: 500 }}>
                {Math.round(slot.width)}×{Math.round(slot.height)}
              </Typography>
            </Box>

            {handles.map(h => (
              <Box
                key={h}
                onMouseDown={(e) => handleSlotMouseDown(e, slot.id, h)}
                sx={{
                  position: 'absolute',
                  width: HANDLE_HIT_PX, height: HANDLE_HIT_PX,
                  bgcolor: 'transparent',
                  cursor: cursors[h], zIndex: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  ...handlePositions[h],
                }}
              >
                <Box sx={{
                  width: HANDLE_VIS_PX, height: HANDLE_VIS_PX,
                  bgcolor: '#1565c0', border: '2px solid white',
                  borderRadius: 0.5, boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                  pointerEvents: 'none',
                }} />
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Main admin page ─────────────────────────────────────────────────────────
const EMPTY_PT_META = { name: '', description: '', bestFor: '', pageBudget: 1, rate: null };

export default function AdminAlbumTemplates() {
  const [searchParams] = useSearchParams();
  const highlightName = searchParams.get('highlight') || '';
  const highlightCardRef = useRef(null);

  // Album template collections (from DB)
  const [albumTemplates, setAlbumTemplates] = useState([]);
  const [loadingAt, setLoadingAt] = useState(true);
  const [selectedAtId, setSelectedAtId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'album'|'page', at, ptId? }
  const [newAtDlg, setNewAtDlg] = useState(false);
  const [newAt, setNewAt] = useState({ name: '', description: '' });
  const [creatingAt, setCreatingAt] = useState(false);

  // Canvas editor state (for adding page templates to a custom album template)
  const [ptMeta, setPtMeta] = useState(EMPTY_PT_META);
  const [slots, setSlots] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [copiedSlot, setCopiedSlot] = useState(null);
  const [editingPtId, setEditingPtId] = useState(null); // non-null = editing existing page template

  // Inline collection rename state
  const [renamingAtId, setRenamingAtId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [ptBudgetFilter, setPtBudgetFilter] = useState('all');

  // The currently selected album template object
  const selectedAt = albumTemplates.find(t => t._id === selectedAtId) || albumTemplates[0] || null;

  const loadAlbumTemplates = useCallback(async () => {
    setLoadingAt(true);
    try {
      const data = await albumService.getAlbumTemplates();
      setAlbumTemplates(data.templates || []);
    } catch (err) {
      setMessage({ severity: 'error', text: 'Failed to load album templates: ' + err.message });
    } finally {
      setLoadingAt(false);
    }
  }, []);

  useEffect(() => { loadAlbumTemplates(); }, [loadAlbumTemplates]);

  // After templates load, if ?highlight=name was passed, find the matching template,
  // select its collection, and open it in the editor.
  useEffect(() => {
    if (!highlightName || loadingAt || albumTemplates.length === 0) return;
    for (const at of albumTemplates) {
      const pt = (at.pageTemplates || []).find(p => p.name === highlightName);
      if (pt) {
        setSelectedAtId(at._id);
        // Give React a tick to render the collection before editing + scrolling
        setTimeout(() => {
          handleEditPageTemplate(pt);
          if (highlightCardRef.current) {
            highlightCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 150);
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightName, loadingAt, albumTemplates.length]);

  // Keyboard: Delete/Backspace removes selected slot; Ctrl+C copies; Ctrl+V pastes
  useEffect(() => {
    const handler = (e) => {
      const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedSlotId && !isInput) {
        setSlots(prev => prev.filter(s => s.id !== selectedSlotId));
        setSelectedSlotId(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedSlotId && !isInput) {
        const slot = slots.find(s => s.id === selectedSlotId);
        if (slot) { setCopiedSlot({ ...slot }); e.preventDefault(); }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && copiedSlot && !isInput) {
        e.preventDefault();
        const id = makeSlotId();
        const clamped = clampSlot(copiedSlot.x + 10, copiedSlot.y + 10, copiedSlot.width, copiedSlot.height);
        setSlots(prev => [...prev, { ...copiedSlot, ...clamped, id, role: copiedSlot.role + '-copy' }]);
        setSelectedSlotId(id);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectedSlotId, slots, copiedSlot]);

  // Canvas event handlers
  const handleAddSlot = useCallback((pageIndex, { x, y, width, height }) => {
    const id = makeSlotId();
    setSlots(prev => {
      const newSlot = { id, pageIndex, role: `zone-${prev.length + 1}`, x, y, width, height, rotate: 0 };
      return [...prev, newSlot];
    });
    setSelectedSlotId(id);
  }, []);

  const handleMoveSlot = useCallback((id, pos) => {
    setSlots(prev => prev.map(s => s.id === id ? { ...s, ...pos } : s));
  }, []);

  const handleResizeSlot = useCallback((id, pos) => {
    setSlots(prev => prev.map(s => s.id === id ? { ...s, ...pos } : s));
  }, []);

  const resetEditor = () => {
    setPtMeta(EMPTY_PT_META);
    setSlots([]);
    setSelectedSlotId(null);
    setEditingPtId(null);
  };

  // Load a page template into the canvas editor for editing
  const handleEditPageTemplate = (pt) => {
    const mmSlots = [];
    (pt.pages || []).forEach(p => {
      (p.zones || []).forEach(z => {
        const { x, y, width, height } = pctToMm(z.xPct, z.yPct, z.wPct, z.hPct);
        mmSlots.push({ id: makeSlotId(), pageIndex: p.pageIndex, role: z.role, x, y, width, height, rotate: z.rotate || 0 });
      });
    });
    setPtMeta({ name: pt.name, description: pt.description || '', bestFor: pt.bestFor || '', pageBudget: pt.pageBudget || 1, rate: pt.rate ?? null });
    setSlots(mmSlots);
    setSelectedSlotId(null);
    setEditingPtId(pt._id);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  // Copy a page template's slots into the canvas editor
  const handleCopyPageTemplate = (pt) => {
    const mmSlots = [];
    if (pt.slotsInMm) {
      pt.slotsInMm.forEach((sl, i) => {
        mmSlots.push({ id: makeSlotId(), pageIndex: 0, role: sl.name || `zone-${i + 1}`, x: sl.x, y: sl.y, width: sl.width, height: sl.height });
      });
    } else {
      (pt.pages || []).forEach(p => {
        (p.zones || []).forEach(z => {
          const { x, y, width, height } = pctToMm(z.xPct, z.yPct, z.wPct, z.hPct);
          mmSlots.push({ id: makeSlotId(), pageIndex: p.pageIndex, role: z.role, x, y, width, height, rotate: z.rotate || 0 });
        });
      });
    }
    setPtMeta({ name: pt.name + ' (copy)', description: pt.description || '', bestFor: pt.bestFor || '', pageBudget: pt.pageBudget || 1, rate: pt.rate ?? null });
    setSlots(mmSlots);
    setSelectedSlotId(null);
    // Switch to first available collection if none is selected.
    if (!selectedAtId && albumTemplates.length > 0) setSelectedAtId(albumTemplates[0]._id);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  // Save (add or update) a page template into the selected custom album template
  const handleSavePageTemplate = async () => {
    if (!selectedAt || selectedAt.isBuiltIn) return;
    if (!ptMeta.name.trim()) { setMessage({ severity: 'warning', text: 'Page template name is required.' }); return; }
    if (slots.filter(s => s.pageIndex === 0).length === 0) { setMessage({ severity: 'warning', text: 'Add at least one image slot on Page 1.' }); return; }

    const pageIndices = ptMeta.pageBudget === 2 ? [0, 1] : [0];
    const pages = pageIndices.map(pi => ({
      pageIndex: pi,
      zones: slots.filter(s => s.pageIndex === pi).map(s => ({
        role: s.role.trim() || 'zone',
        ...mmToPct(s.x, s.y, s.width, s.height),
        rotate: s.rotate || 0,
      })),
    }));

    setSaving(true);
    try {
      let updated;
      if (editingPtId) {
        updated = await albumService.updatePageTemplate(selectedAt._id, editingPtId, {
          name: ptMeta.name.trim(), description: ptMeta.description.trim(),
          bestFor: ptMeta.bestFor.trim(), pageBudget: ptMeta.pageBudget, pages,
          rate: ptMeta.rate ?? null,
        });
        setMessage({ severity: 'success', text: `Page template "${ptMeta.name}" updated.` });
      } else {
        updated = await albumService.addPageTemplate(selectedAt._id, {
          name: ptMeta.name.trim(), description: ptMeta.description.trim(),
          bestFor: ptMeta.bestFor.trim(), pageBudget: ptMeta.pageBudget, pages,
          rate: ptMeta.rate ?? null,
        });
        setMessage({ severity: 'success', text: `Page template "${ptMeta.name}" added.` });
      }
      setAlbumTemplates(prev => prev.map(at => at._id === selectedAt._id ? updated.template : at));
      resetEditor();
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  // Remove a page template from a custom album template
  const handleDeletePageTemplate = async (at, ptId) => {
    try {
      const updated = await albumService.removePageTemplate(at._id, ptId);
      setAlbumTemplates(prev => prev.map(a => a._id === at._id ? updated.template : a));
      setMessage({ severity: 'success', text: 'Page template removed.' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.message });
    }
    setDeleteConfirm(null);
  };

  // Create a new album template collection
  const handleCreateAlbumTemplate = async () => {
    if (!newAt.name.trim()) return;
    setCreatingAt(true);
    try {
      const data = await albumService.createAlbumTemplate({ name: newAt.name.trim(), description: newAt.description.trim() });
      setAlbumTemplates(prev => [...prev, data.template]);
      setSelectedAtId(data.template._id);
      setNewAtDlg(false);
      setNewAt({ name: '', description: '' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setCreatingAt(false);
    }
  };

  // Commit inline collection rename
  const handleCommitRename = async () => {
    if (!renameValue.trim() || !renamingAtId) return;
    setRenameSaving(true);
    try {
      const data = await albumService.updateAlbumTemplate(renamingAtId, { name: renameValue.trim() });
      setAlbumTemplates(prev => prev.map(at => at._id === renamingAtId ? data.template : at));
      setMessage({ severity: 'success', text: 'Collection renamed.' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setRenameSaving(false);
      setRenamingAtId(null);
    }
  };

  // Toggle isEnabled on a page template
  const handleTogglePageTemplate = async (at, pt) => {
    const newEnabled = pt.isEnabled === false ? true : false;
    try {
      const updated = await albumService.updatePageTemplate(at._id, pt._id, {
        name: pt.name, description: pt.description || '', bestFor: pt.bestFor || '',
        pageBudget: pt.pageBudget || 1, rate: pt.rate ?? null,
        pages: pt.pages, isEnabled: newEnabled,
      });
      setAlbumTemplates(prev => prev.map(a => a._id === at._id ? updated.template : a));
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.error || err.message });
    }
  };

  // Delete an entire album template collection
  const handleDeleteAlbumTemplate = async (atId) => {
    try {
      await albumService.deleteAlbumTemplate(atId);
      setAlbumTemplates(prev => prev.filter(t => t._id !== atId));
      if (selectedAtId === atId) {
        const remaining = albumTemplates.filter(t => t._id !== atId);
        setSelectedAtId(remaining.length > 0 ? remaining[0]._id : null);
      }
      setMessage({ severity: 'success', text: 'Album template collection deleted.' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.message });
    }
    setDeleteConfirm(null);
  };

  const page0Slots = slots.filter(s => s.pageIndex === 0);
  const page1Slots = slots.filter(s => s.pageIndex === 1);
  const selectedSlotObj = slots.find(s => s.id === selectedSlotId) || null;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 80px)', overflow: 'hidden' }}>

      {/* ── Left Sidebar: Album Template Collections ── */}
      <Paper elevation={0} sx={{ width: 230, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, pb: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CollectionIcon sx={{ fontSize: 16 }} /> Album Templates
            </Typography>
            <Tooltip title="New collection">
              <IconButton size="small" onClick={() => setNewAtDlg(true)}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        <List dense sx={{ flex: 1, overflow: 'auto', pt: 0.5 }}>
          {albumTemplates.length > 0 && (
            <ListSubheader sx={{ lineHeight: '24px', fontSize: 10, bgcolor: 'transparent', mt: 0.5 }}>COLLECTIONS</ListSubheader>
          )}
          {loadingAt && <Box sx={{ px: 2, py: 1 }}><CircularProgress size={16} /></Box>}
          {albumTemplates.map(at => (
            <ListItemButton
              key={at._id}
              selected={selectedAtId === at._id}
              onClick={() => setSelectedAtId(at._id)}
              sx={{ borderRadius: 1, mx: 0.5 }}
            >
              <ListItemText
                primary={at.name}
                secondary={`${(at.pageTemplates || []).length} templates`}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Tooltip title="Delete collection">
                <IconButton
                  size="small" color="error"
                  onMouseDown={e => { e.stopPropagation(); setDeleteConfirm({ type: 'album', at }); }}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* ── Main content area ── */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}

        {selectedAt && (
          <>
            {/* Album Template header */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                {renamingAtId !== selectedAt._id ? (
                  <>
                    <Typography variant="h6" fontWeight={700}>{selectedAt.name}</Typography>
                    <Tooltip title="Rename collection">
                      <IconButton size="small" onClick={() => { setRenamingAtId(selectedAt._id); setRenameValue(selectedAt.name); }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                      size="small" value={renameValue} autoFocus
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleCommitRename(); if (e.key === 'Escape') setRenamingAtId(null); }}
                      sx={{ width: 260 }}
                    />
                    <Tooltip title="Save name">
                      <span>
                        <IconButton size="small" color="primary" onClick={handleCommitRename} disabled={renameSaving || !renameValue.trim()}>
                          {renameSaving ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={() => setRenamingAtId(null)}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">{selectedAt.description}</Typography>
            </Box>

            {/* Page templates grid */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PageIcon sx={{ fontSize: 18 }} />
                  Page Templates ({(selectedAt.pageTemplates || []).length})
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary">Filter:</Typography>
                  {[['all', 'All'], [1, '1-page'], [2, '2-page spreads']].map(([val, label]) => (
                    <Chip
                      key={val}
                      size="small"
                      label={label}
                      color={ptBudgetFilter === val ? (val === 2 ? 'secondary' : 'primary') : 'default'}
                      variant={ptBudgetFilter === val ? 'filled' : 'outlined'}
                      onClick={() => setPtBudgetFilter(val)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
              </Stack>
              {(selectedAt.pageTemplates || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No page templates yet. Use the canvas editor below to add the first one.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {(selectedAt.pageTemplates || [])
                    .filter(pt => ptBudgetFilter === 'all' || pt.pageBudget === ptBudgetFilter)
                    .map((pt) => {
                    const isSpread = (pt.pageBudget ?? 1) >= 2;
                    const ptTotalZones = (pt.pages || []).reduce((s, p) => s + (p.zones || []).length, 0);
                    const ptDisplayRate = pt.rate ?? (ptTotalZones <= 1 ? 1 : ptTotalZones <= 2 ? 2 : ptTotalZones <= 4 ? 3 : ptTotalZones <= 6 ? 4 : 5);
                    const rateColorMap = { 1: 'success', 2: 'info', 3: 'primary', 4: 'warning', 5: 'error' };
                    return (
                      <Paper
                        key={pt._id}
                        ref={pt.name === highlightName ? highlightCardRef : null}
                        variant="outlined"
                        sx={{
                          p: 1, width: isSpread ? 220 : 140, display: 'flex', flexDirection: 'column',
                          alignItems: 'center', gap: 0.5, opacity: pt.isEnabled === false ? 0.45 : 1,
                          position: 'relative',
                          ...(pt.name === highlightName && {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            boxShadow: '0 0 0 4px rgba(25,118,210,0.15)',
                          }),
                        }}
                      >
                        {pt.isEnabled === false && (
                          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '3px', zIndex: 1, pointerEvents: 'none' }} />
                        )}
                        <SpreadThumb pt={pt} size={isSpread ? 216 : 110} />
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', justifyContent: 'center' }}>
                          <Chip
                            label={isSpread ? '2-page spread' : '1-page'}
                            size="small"
                            color={isSpread ? 'secondary' : 'default'}
                            variant="filled"
                            sx={{ fontSize: 9, height: 16 }}
                          />
                          <Tooltip title={`AI rate: 1=1 img, 2=2 imgs, 3=3-4 imgs, 4=5-6 imgs, 5=7+ imgs${pt.rate == null ? '. Auto-computed from slot count.' : '. Manually set.'}`}>
                            <Chip
                              label={`${ptDisplayRate}${pt.rate == null ? '★' : ''}`}
                              size="small"
                              color={rateColorMap[ptDisplayRate]}
                              variant={pt.rate == null ? 'outlined' : 'filled'}
                              sx={{ fontSize: 9, height: 16, minWidth: 28 }}
                            />
                          </Tooltip>
                        </Stack>
                        <Typography variant="caption" fontWeight={600} align="center" noWrap sx={{ width: '100%', textAlign: 'center' }}>
                          {pt.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ fontSize: 10 }}>
                          {pt.description}
                        </Typography>
                        {pt.bestFor && <Chip label={pt.bestFor} size="small" sx={{ fontSize: 9, height: 16 }} />}
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                          <Tooltip title={pt.isEnabled === false ? 'Enable template (currently disabled — AI will not use it)' : 'Disable template (AI will skip it)'}>
                            <IconButton size="small" color={pt.isEnabled === false ? 'default' : 'success'} onClick={() => handleTogglePageTemplate(selectedAt, pt)}>
                              {pt.isEnabled === false ? <VisibilityOffIcon sx={{ fontSize: 14 }} /> : <VisibilityIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy into editor">
                            <IconButton size="small" onClick={() => handleCopyPageTemplate(pt)}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit layout">
                            <IconButton size="small" color="primary" onClick={() => handleEditPageTemplate(pt)}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ type: 'page', at: selectedAt, ptId: pt._id })}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Paper>

            {/* Canvas editor */}
            {selectedAt && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {editingPtId ? `Edit Page Template in "${selectedAt.name}"` : `Add Page Template to "${selectedAt.name}"`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Click+drag on the canvas to draw image slots. The grey title strip at the top is reserved.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Canvas area */}
                  <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight={600}>Pages:</Typography>
                      <ToggleButtonGroup size="small" exclusive value={ptMeta.pageBudget}
                        onChange={(_, v) => { if (v) setPtMeta(m => ({ ...m, pageBudget: v })); }}
                      >
                        <ToggleButton value={1}>Single</ToggleButton>
                        <ToggleButton value={2}>Two-page spread</ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                    <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                      <PageCanvas slots={page0Slots} selectedId={selectedSlotId}
                        onSelectSlot={setSelectedSlotId} onAddSlot={handleAddSlot}
                        onMoveSlot={handleMoveSlot} onResizeSlot={handleResizeSlot} pageIndex={0}
                      />
                      {ptMeta.pageBudget === 2 && (
                        <PageCanvas slots={page1Slots} selectedId={selectedSlotId}
                          onSelectSlot={setSelectedSlotId} onAddSlot={handleAddSlot}
                          onMoveSlot={handleMoveSlot} onResizeSlot={handleResizeSlot} pageIndex={1}
                        />
                      )}
                    </Stack>
                    {/* Slot list */}
                    {slots.length > 0 && (
                      <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                        <Stack spacing={0.5}>
                          {slots.map((s, si) => (
                            <Stack key={s.id} direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: SLOT_COLORS[si % SLOT_COLORS.length], border: '1px solid #ccc', flexShrink: 0 }} />
                              <TextField size="small" label="Role" value={s.role}
                                onChange={e => setSlots(prev => prev.map(sl => sl.id === s.id ? { ...sl, role: e.target.value } : sl))}
                                inputProps={{ style: { fontSize: 12, padding: '2px 6px' } }}
                                sx={{ width: 120 }}
                              />
                              <TextField size="small" label="Rotate°" value={s.rotate || 0}
                                onChange={e => { const v = parseInt(e.target.value, 10); setSlots(prev => prev.map(sl => sl.id === s.id ? { ...sl, rotate: isNaN(v) ? 0 : Math.max(-15, Math.min(15, v)) } : sl)); }}
                                inputProps={{ style: { fontSize: 12, padding: '2px 6px' }, type: 'number', min: -15, max: 15 }}
                                sx={{ width: 72 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                Pg{s.pageIndex + 1} {Math.round(s.width)}×{Math.round(s.height)}mm
                              </Typography>
                              <IconButton size="small" onClick={() => {
                                setSlots(p => p.filter(sl => sl.id !== s.id));
                                if (selectedSlotId === s.id) setSelectedSlotId(null);
                              }}>
                                <DeleteIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Box>

                  {/* Controls panel */}
                  <Paper variant="outlined" sx={{ width: 260, flexShrink: 0, p: 1.5 }}>
                    <Stack spacing={1.5}>
                      <TextField label="Name *" size="small" fullWidth value={ptMeta.name}
                        onChange={e => setPtMeta(m => ({ ...m, name: e.target.value }))} />
                      <TextField label="Description" size="small" fullWidth value={ptMeta.description}
                        onChange={e => setPtMeta(m => ({ ...m, description: e.target.value }))} />
                      <TextField label="Best For (LLM hint)" size="small" fullWidth value={ptMeta.bestFor}
                        onChange={e => setPtMeta(m => ({ ...m, bestFor: e.target.value }))}
                        helperText="e.g. '2 landscape + 1 portrait'" />
                      <Tooltip title="Rate 1=1 img, 2=2 imgs, 3=3-4 imgs, 4=5-6 imgs, 5=7+ imgs. Auto = computed from slot count. Lower rate = AI prefers it when images fit naturally.">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>AI Rate:</Typography>
                          <ToggleButtonGroup size="small" exclusive
                            value={ptMeta.rate}
                            onChange={(_, v) => setPtMeta(m => ({ ...m, rate: v }))}
                          >
                            <ToggleButton value={null} sx={{ fontSize: 11, px: 0.75 }}>Auto</ToggleButton>
                            <ToggleButton value={1} sx={{ fontSize: 11, px: 0.75 }}>1</ToggleButton>
                            <ToggleButton value={2} sx={{ fontSize: 11, px: 0.75 }}>2</ToggleButton>
                            <ToggleButton value={3} sx={{ fontSize: 11, px: 0.75 }}>3</ToggleButton>
                            <ToggleButton value={4} sx={{ fontSize: 11, px: 0.75 }}>4</ToggleButton>
                            <ToggleButton value={5} sx={{ fontSize: 11, px: 0.75 }}>5</ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>
                      </Tooltip>
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={1}>
                      {selectedSlotObj && (
                        <>
                          <Button size="small" variant="outlined" startIcon={<CopyIcon />}
                            onClick={() => setCopiedSlot({ ...selectedSlotObj })} fullWidth>
                            Copy Slot (Ctrl+C)
                          </Button>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                            onClick={() => { setSlots(p => p.filter(s => s.id !== selectedSlotId)); setSelectedSlotId(null); }} fullWidth>
                            Delete Selected Slot
                          </Button>
                        </>
                      )}
                      {copiedSlot && (
                        <Button size="small" variant="outlined" color="secondary" startIcon={<ContentPasteIcon />}
                          onClick={() => {
                            const id = makeSlotId();
                            const clamped = clampSlot(copiedSlot.x + 10, copiedSlot.y + 10, copiedSlot.width, copiedSlot.height);
                            setSlots(prev => [...prev, { ...copiedSlot, ...clamped, id, role: copiedSlot.role + '-copy' }]);
                            setSelectedSlotId(id);
                          }} fullWidth>
                          Paste Slot (Ctrl+V)
                        </Button>
                      )}
                      <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={resetEditor} fullWidth disabled={slots.length === 0}>
                        Clear
                      </Button>
                      <Button variant="contained" startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}
                        onClick={handleSavePageTemplate} fullWidth disabled={saving || slots.length === 0 || !ptMeta.name.trim()}>
                        {editingPtId ? 'Save Changes' : 'Add Page Template'}
                      </Button>
                    </Stack>
                  </Paper>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* ── Create Album Template dialog ── */}
      <Dialog open={newAtDlg} onClose={() => setNewAtDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Album Template Collection</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Collection Name *" size="small" fullWidth value={newAt.name}
              onChange={e => setNewAt(n => ({ ...n, name: e.target.value }))}
              helperText="e.g. 'Modern Minimalist', 'Classic Wedding'" />
            <TextField label="Description" size="small" fullWidth multiline rows={2} value={newAt.description}
              onChange={e => setNewAt(n => ({ ...n, description: e.target.value }))}
              helperText="Describes the overall visual style" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewAtDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateAlbumTemplate} disabled={creatingAt || !newAt.name.trim()}>
            {creatingAt ? <CircularProgress size={16} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation dialog ── */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {deleteConfirm?.type === 'album' ? 'Delete Album Template Collection?' : 'Remove Page Template?'}
        </DialogTitle>
        <DialogContent>
          {deleteConfirm?.type === 'album'
            ? <Typography>Delete <strong>{deleteConfirm.at.name}</strong> and all its page templates? This cannot be undone.</Typography>
            : <Typography>Remove this page template from <strong>{deleteConfirm?.at?.name}</strong>?</Typography>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            if (deleteConfirm.type === 'album') handleDeleteAlbumTemplate(deleteConfirm.at._id);
            else handleDeletePageTemplate(deleteConfirm.at, deleteConfirm.ptId);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}



